import { Field, Form, Formik, type FormikProps } from 'formik'
import difference from 'lodash-es/difference'
import isEmpty from 'lodash-es/isEmpty'
import isEqual from 'lodash-es/isEqual'
import unescape from 'lodash-es/unescape'
import React, { type ComponentProps, useRef } from 'react'

import { Dashboard, PageLayout, SideSummary } from '@components/common'
import { UnavailableResource } from '@components/common/UnavailableResource'
import { StatusTagDropdown } from '@components/composed'
import { SlugChangeModal } from '@components/modals/SlugChangeModal'
import { DashboardPermissionGuard } from '@components/utils/DashboardPermissions'
import { LeavingGuard } from '@components/utils/LeavingGuard'
import {
  BackButton,
  Button,
  Checkbox,
  type CheckboxProps,
  ConfirmModal,
  Dropdown,
  Fieldset,
  HeadContent,
  Icon,
  Input,
  Loading,
  PositionChangeModal,
  SaveShortcutListener,
  Subsection,
  ToolbarButton,
  useMediaQueryContext,
  useModalContext,
} from '@fourthwall/components'
import { DotsHorizontalIcon, PlusIcon } from '@fourthwall/icons'
import type { Offer } from '@fourthwall/services/dist/models/offer'
import { arrayMove } from '@fourthwall/utils/dist/array'
import { pluralize } from '@fourthwall/utils/dist/string'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'
import { SelectProductsModal } from '@modules/Dashboard/modules/SelectProductsModal'
import { routing } from '@utils/routing'
import { addProtocol } from '@utils/url'

import { TimezonedDatePicker } from '../../Timezone/components/TimezonedDatePicker'
import { SelectedProducts } from './components/SelectedProducts'
import { SortingStrategySelect } from './components/SortingStrategySelect'
import { StatusConfirmationModal } from './components/StatusConfirmationModal'
import { validationSchema } from './consts'
import * as S from './styled'
import type { CollectionViewProps, FormValues } from './types'
import { getCollectionTimeFrame, getShouldDisplayStatusConfirmation } from './utils'

export const CollectionView = ({
  initialValues,
  collection,
  offers,
  shopBaseUri,
  isFetching,
  isCreating,
  isSubmitting,
  isError,
  isEditable,
  hasDropProducts,
  totalSold,
  setSelectedOfferIds,
  onSubmit,
  onStatusSubmit,
  onSlugChange,
  onCollectionDelete,
}: CollectionViewProps) => {
  const { open } = useModalContext()
  const media = useMediaQueryContext()
  const formikRef = useRef<FormikProps<FormValues>>(null)

  const isFormLoading = isSubmitting || (!isCreating && isFetching)
  const collectionUrl = `${shopBaseUri}/collections/${collection?.slug}`

  const getPageTitle = () => {
    return isCreating ? 'Create new collection' : unescape(collection?.name)
  }

  const handleFormSubmit = async (values: FormValues) => {
    const getNewProductsStatus = () => {
      if (values.isSoldOut && collection?.state.status === 'HIDDEN') {
        return 'HIDDEN'
      }

      if (values.isTimeLimited) {
        return 'PUBLIC'
      }

      return collection?.state.status
    }

    const productsAreEmpty = isEmpty(formikRef.current?.values.selectedProducts)
    const newTimeFrame = getCollectionTimeFrame(
      values.isTimeLimited,
      values.startDate,
      values.endDate,
    )

    if (!isCreating && collection && !productsAreEmpty) {
      const newStatus = getNewProductsStatus()
      const productsHasChanged = difference(values.selectedProducts, collection?.offerIds).length
      const timeFrameHasChanged = !isEqual(newTimeFrame, collection?.timeFrame)

      if (productsHasChanged || timeFrameHasChanged) {
        return open(StatusConfirmationModal, {
          newStatus,
          startDate: newStatus === 'PUBLIC' ? values.startDate : undefined,
          endDate: newStatus === 'PUBLIC' ? values.endDate : undefined,
          onConfirm: (withStatusUpdate) => onSubmit(values, withStatusUpdate),
        })
      }
    }

    await onSubmit(values, false)
  }

  const handleCollectionDelete = () => {
    open(ConfirmModal, {
      title: `Are you sure that you want to delete "${collection?.name}" collection?`,
      confirmLabel: 'Delete',
      confirmAppearance: 'destructive',
      onConfirm: onCollectionDelete,
    })
  }

  const handleSlugChange = () => {
    open(SlugChangeModal, {
      title: 'Edit collection URL',
      slug: collection?.slug || '',
      shopBaseUri: shopBaseUri || '',
      prepend: 'collections',
      onSubmit: onSlugChange,
    })
  }

  const handleStatusTagDropdownSubmit: ComponentProps<typeof StatusTagDropdown>['onSubmit'] = (
    values,
  ) => {
    if (!formikRef.current) return

    const { selectedProducts } = formikRef.current.values

    const selectedOffers: (Offer.Item | undefined)[] = offers
      ? selectedProducts.map((id) => offers[id])
      : []

    const shouldDisplayStatusConfirmation = getShouldDisplayStatusConfirmation(
      values.status,
      selectedOffers,
    )

    if (!shouldDisplayStatusConfirmation) {
      return onStatusSubmit(values, true)
    }

    if (values.status === 'SCHEDULE') {
      return open(StatusConfirmationModal, {
        startDate: values.startDate,
        endDate: values.setEndDate ? values.endDate : undefined,
        onConfirm: (withStatusUpdate) => onStatusSubmit(values, withStatusUpdate),
      })
    }

    return open(StatusConfirmationModal, {
      newStatus: values.status,
      onConfirm: (withStatusUpdate) => onStatusSubmit(values, withStatusUpdate),
    })
  }

  const renderStatus = () => {
    if (isCreating || !collection) return null

    return (
      <StatusTagDropdown
        status={collection.state.status}
        timeFrame={collection.timeFrame}
        isSoldOut={!collection.available}
        disabled={!isEditable}
        onSubmit={handleStatusTagDropdownSubmit}
      />
    )
  }

  const renderToolbarContent = () => {
    if (isCreating || !collection || !isEditable) return null

    return (
      <Dropdown
        content={
          <>
            <Dropdown.Item label="Change URL" onClick={handleSlugChange} />
            <Dropdown.Item label="Delete collection" onClick={handleCollectionDelete} />
          </>
        }
        position="bottom-end"
      >
        <ToolbarButton
          label="Settings"
          icon={DotsHorizontalIcon}
          size={media.tablet ? 'medium' : 'small'}
        />
      </Dropdown>
    )
  }

  const handleAddProducts = () => {
    if (!formikRef.current) return

    open(SelectProductsModal, {
      selectedIds: formikRef.current.values.selectedProducts,
      source: 'listing',
      onSave: (selectedProductsIds) => {
        if (!formikRef.current) return

        formikRef.current.setFieldValue('selectedProducts', selectedProductsIds)
        setSelectedOfferIds(selectedProductsIds)
      },
    })
  }

  const handleItemRemove = (index: number) => {
    if (!formikRef.current) return

    formikRef.current.setFieldValue(
      'selectedProducts',
      formikRef.current.values.selectedProducts.filter((item, itemIndex) => itemIndex !== index),
    )
  }

  const handleSortChange = (newIndex: number, oldIndex: number) => {
    if (!formikRef.current) return

    formikRef.current.setFieldValue(
      'selectedProducts',
      arrayMove(formikRef.current.values.selectedProducts, newIndex, oldIndex),
    )
  }

  const handlePositionChange = (index: number) => {
    if (!formikRef.current) return

    open(PositionChangeModal, {
      title: 'Change product position',
      maxPosition: formikRef.current.values.selectedProducts.length,
      currentPosition: index + 1,
      onSubmit: (values) => {
        if (!formikRef.current) return

        const { type = 'EXACT' } = values
        const toIndex = {
          TOP: 0,
          BOTTOM: formikRef.current.values.selectedProducts.length - 1,
          EXACT: values.exactPosition - 1,
        }[type]

        return formikRef.current.setFieldValue(
          'selectedProducts',
          arrayMove(formikRef.current.values.selectedProducts, index, toIndex),
        )
      },
    })
  }

  const renderAnalyticsSummary = () => {
    if (isCreating || !totalSold) {
      return 'No sales yet'
    }

    return `${totalSold} ${pluralize(totalSold, 'product')} sold`
  }

  if (isError) {
    return <UnavailableResource resource="collection" />
  }

  const getTimeLimitedCheckboxProps = (): CheckboxProps => {
    if (isCreating) {
      return {
        disabled: true,
        tooltip: 'You need to save your collection before you can make it time-limited.',
      }
    }

    if (hasDropProducts) {
      return {
        disabled: true,
        tooltip: 'You cannot set up time-limited collection that includes pre-sales offers.',
      }
    }

    return { disabled: !isEditable }
  }

  const getSoldOutCheckboxProps = (): CheckboxProps => {
    if (isCreating) {
      return {
        disabled: true,
        tooltip: 'You need to save your collection before you can mark it as sold out.',
      }
    }

    return { disabled: !isEditable }
  }

  const getSortingStrategySelectProps = () => {
    if (isCreating) {
      return {
        disabled: true,
        helperText: 'You need to save your collection before you can change sorting strategy.',
      }
    }

    return { disabled: !isEditable }
  }

  return (
    <Formik<FormValues>
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={handleFormSubmit}
    >
      {(formik) => (
        <Form>
          <HeadContent title={getPageTitle()} />
          <Loading isLoading={isFormLoading}>
            <PageLayout
              rightContent={
                <DashboardPermissionGuard permission="contributions.profit">
                  <SideSummary>
                    <SideSummary.Item title="Analytics">
                      {renderAnalyticsSummary()}
                    </SideSummary.Item>
                  </SideSummary>
                </DashboardPermissionGuard>
              }
              leftContent={
                <>
                  <Subsection title="Collection details">
                    <Fieldset>
                      <Fieldset.Row>
                        <Fieldset.Item>
                          <Field
                            component={Input}
                            name="name"
                            label="Collection name"
                            required
                            disabled={!isEditable}
                          />
                        </Fieldset.Item>
                      </Fieldset.Row>
                      <Fieldset.Row>
                        <Field
                          component={Checkbox}
                          name="isTimeLimited"
                          label="Make this a limited-time only collection"
                          description="Set a start time for when the products should be listed as public, and an end time for when they should be marked as sold out."
                          {...getTimeLimitedCheckboxProps()}
                        />
                      </Fieldset.Row>
                      {formik.values.isTimeLimited && (
                        <Fieldset.Row inner>
                          <TimezonedDatePicker
                            type="range"
                            value={[formik.values.startDate, formik.values.endDate]}
                            disabled={!isEditable}
                            showTimeInputs
                            onChange={([startDate, endDate]) => {
                              formik.setFieldValue('startDate', startDate)
                              formik.setFieldValue('endDate', endDate)
                            }}
                          />
                        </Fieldset.Row>
                      )}
                      <Fieldset.Row>
                        <Field
                          component={Checkbox}
                          name="isSoldOut"
                          label="Mark products as sold out"
                          description="This will override any time-limits set on products."
                          {...getSoldOutCheckboxProps()}
                        />
                      </Fieldset.Row>
                      <Fieldset.Row>
                        <Fieldset.Item>
                          <SortingStrategySelect
                            name="sortingStrategy"
                            label="Product sorting"
                            value={formik.values.sortingStrategy}
                            onValueChange={(value) =>
                              formik.setFieldValue('sortingStrategy', value)
                            }
                            {...getSortingStrategySelectProps()}
                          />
                        </Fieldset.Item>
                      </Fieldset.Row>
                    </Fieldset>
                  </Subsection>
                  <Subsection title="Products">
                    <S.ProductsHeader>
                      <S.CountWrapper>
                        <S.Count>
                          <>
                            {formik.values.selectedProducts.length || 'No'}{' '}
                            {pluralize(formik.values.selectedProducts.length, 'product')} added
                          </>
                        </S.Count>
                      </S.CountWrapper>
                      <Button
                        label="Add products"
                        size="small"
                        disabled={!isEditable}
                        leftIcon={<Icon component={PlusIcon} height={12} />}
                        onClick={handleAddProducts}
                      />
                    </S.ProductsHeader>
                    <S.SelectedProductsContainer isEditable={isEditable}>
                      <SelectedProducts
                        collectionName={collection?.name || 'Collection'}
                        offers={offers}
                        selectedProductsIds={formik.values.selectedProducts}
                        disableSorting={!isEditable || formik.values.sortingStrategy !== 'MANUAL'}
                        onRemove={handleItemRemove}
                        onSortChange={handleSortChange}
                        onPositionChange={handlePositionChange}
                      />
                    </S.SelectedProductsContainer>
                  </Subsection>
                </>
              }
            >
              <div>
                <Dashboard.ItemViewHeader
                  name={getPageTitle()}
                  link={
                    !isCreating
                      ? {
                          label: addProtocol(collectionUrl),
                          url: addProtocol(collectionUrl),
                          copyUrl: addProtocol(collectionUrl),
                        }
                      : undefined
                  }
                  backButton={
                    <BackButton label="Collections" to={routing.products.collections.self} />
                  }
                  status={renderStatus()}
                  toolbar={renderToolbarContent()}
                />
              </div>
            </PageLayout>
          </Loading>
          <SaveShortcutListener disabled={!formik.dirty} onSave={formik.submitForm} />
          <ConfirmBar
            isOpen={formik.dirty}
            isLoading={isFormLoading}
            onConfirm={formik.submitForm}
            onCancel={formik.resetForm}
          />
          <LeavingGuard when={formik.dirty} isValid={formik.isValid} onSave={formik.submitForm} />
        </Form>
      )}
    </Formik>
  )
}
